import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Upload, Loader2, Sparkles } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const CATEGORIES = ['Comprar', 'Vender', 'Rentar', 'Mercado Guatemala'];

const BlogFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    meta_description: '',
    cover_image: '',
    content: '',
    category: 'Comprar',
    is_published: false,
    author: 'Ikonic Deals'
  });

  useEffect(() => {
    if (isEditing) {
      fetchArticle();
    }
  }, [id]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/blog/articles/${id}?increment_views=false`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Error al cargar el artículo');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await apiClient.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData({ ...formData, cover_image: imageUrl });
      toast.success('Imagen subida correctamente');
    } catch (error) {
      toast.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await apiClient.put(`/blog/articles/${id}`, formData);
        toast.success('Artículo actualizado');
      } else {
        await apiClient.post('/blog/articles', formData);
        toast.success('Artículo creado');
      }
      navigate('/admin/blog');
    } catch (error) {
      toast.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el artículo`);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    // Save to localStorage for preview
    localStorage.setItem('blog_preview', JSON.stringify(formData));
    window.open('/blog/preview', '_blank');
  };

  // Simple rich text formatting helpers
  const insertFormat = (tag) => {
    const textarea = document.getElementById('content-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    let newText;
    switch(tag) {
      case 'h2':
        newText = `<h2>${selectedText || 'Título de sección'}</h2>`;
        break;
      case 'h3':
        newText = `<h3>${selectedText || 'Subtítulo'}</h3>`;
        break;
      case 'bold':
        newText = `<strong>${selectedText || 'texto en negrita'}</strong>`;
        break;
      case 'italic':
        newText = `<em>${selectedText || 'texto en cursiva'}</em>`;
        break;
      case 'link':
        newText = `<a href="URL_AQUI">${selectedText || 'texto del enlace'}</a>`;
        break;
      case 'ul':
        newText = `<ul>\n  <li>${selectedText || 'Elemento 1'}</li>\n  <li>Elemento 2</li>\n</ul>`;
        break;
      case 'ol':
        newText = `<ol>\n  <li>${selectedText || 'Paso 1'}</li>\n  <li>Paso 2</li>\n</ol>`;
        break;
      case 'quote':
        newText = `<blockquote>${selectedText || 'Cita destacada'}</blockquote>`;
        break;
      case 'p':
        newText = `<p>${selectedText || 'Párrafo de texto'}</p>`;
        break;
      default:
        newText = selectedText;
    }
    
    const newContent = formData.content.substring(0, start) + newText + formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <form onSubmit={handleSubmit} className="p-6 max-w-5xl" data-testid="blog-form">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/blog')}
              className="text-[#A3A3A3] hover:text-white"
            >
              <ArrowLeft size={16} className="mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl text-white font-medium">
              {isEditing ? 'Editar Artículo' : 'Nuevo Artículo'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              disabled={!formData.title}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Eye size={16} className="mr-2" />
              Vista Previa
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#D4AF37] text-black hover:bg-[#FFE082]"
              data-testid="save-article-btn"
            >
              {saving ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              {isEditing ? 'Guardar Cambios' : 'Crear Artículo'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <Label className="text-[#A3A3A3] mb-2 block">Título del artículo *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Guía completa para comprar tu primer apartamento"
                className="h-12 bg-black/40 border-white/10 text-white text-lg"
                data-testid="article-title-input"
              />
            </div>

            {/* Meta Description */}
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <Label className="text-[#A3A3A3] mb-2 block">Meta descripción (SEO)</Label>
              <Textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="Descripción breve que aparecerá en Google (150-160 caracteres recomendados)"
                className="bg-black/40 border-white/10 text-white resize-none"
                rows={3}
              />
              <p className="text-[#666] text-xs mt-2">
                {formData.meta_description.length}/160 caracteres
              </p>
            </div>

            {/* Content Editor */}
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-[#A3A3A3]">Contenido del artículo</Label>
                <div className="flex items-center gap-1 bg-black/40 rounded-lg p-1">
                  <button type="button" onClick={() => insertFormat('h2')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Título H2">H2</button>
                  <button type="button" onClick={() => insertFormat('h3')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Subtítulo H3">H3</button>
                  <button type="button" onClick={() => insertFormat('p')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Párrafo">P</button>
                  <button type="button" onClick={() => insertFormat('bold')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded font-bold" title="Negrita">B</button>
                  <button type="button" onClick={() => insertFormat('italic')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded italic" title="Cursiva">I</button>
                  <button type="button" onClick={() => insertFormat('link')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Enlace">🔗</button>
                  <button type="button" onClick={() => insertFormat('ul')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Lista">• •</button>
                  <button type="button" onClick={() => insertFormat('ol')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Lista numerada">1.</button>
                  <button type="button" onClick={() => insertFormat('quote')} className="px-2 py-1 text-xs text-white hover:bg-white/10 rounded" title="Cita">"</button>
                </div>
              </div>
              <Textarea
                id="content-editor"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="<p>Escribe el contenido de tu artículo aquí...</p>"
                className="bg-black/40 border-white/10 text-white font-mono text-sm min-h-[400px] resize-y"
                data-testid="article-content-input"
              />
              <p className="text-[#666] text-xs mt-2">
                Usa HTML para dar formato: &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;a href=""&gt;, etc.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <h3 className="text-white font-medium mb-4">Publicación</h3>
              
              <div className="flex items-center justify-between mb-4">
                <Label className="text-[#A3A3A3]">Publicar artículo</Label>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-[#A3A3A3] mb-2 block">Categoría</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="bg-black/40 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat} className="text-white hover:bg-white/10">
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#A3A3A3] mb-2 block">Autor</Label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Cover Image */}
            <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <h3 className="text-white font-medium mb-4">Imagen de portada</h3>
              
              {formData.cover_image ? (
                <div className="relative mb-4">
                  <img
                    src={formData.cover_image}
                    alt="Cover"
                    className="w-full aspect-[16/9] object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setFormData({ ...formData, cover_image: '' })}
                  >
                    Eliminar
                  </Button>
                </div>
              ) : (
                <div 
                  className="w-full aspect-[16/9] bg-black/40 border border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37]/50 transition-colors mb-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-[#666] mb-2" />
                      <p className="text-[#666] text-sm">Haz clic para subir</p>
                    </>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />

              <div className="mt-3">
                <Label className="text-[#A3A3A3] mb-2 block text-xs">O ingresa URL de imagen</Label>
                <Input
                  value={formData.cover_image}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  placeholder="https://..."
                  className="bg-black/40 border-white/10 text-white text-sm"
                />
              </div>
            </div>

            {/* Tips */}
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4">
              <h4 className="text-[#D4AF37] font-medium text-sm mb-2 flex items-center gap-2">
                <Sparkles size={14} />
                Tips para SEO
              </h4>
              <ul className="text-[#A3A3A3] text-xs space-y-1">
                <li>• Usa títulos descriptivos (60-70 caracteres)</li>
                <li>• Meta descripción de 150-160 caracteres</li>
                <li>• Incluye palabras clave relevantes</li>
                <li>• Usa subtítulos H2 y H3 para estructurar</li>
                <li>• Agrega imágenes de alta calidad</li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default BlogFormPage;
